import type { BillDiscount, DiscountOffer, Sale } from "./types";
import { DISCOUNT_MODE, EXTERNAL_SOURCE } from "./vocabulary";

/**
 * A discount's identity: is this offer the one on the bill, which entry does a remove, a replace or
 * a release act on (DISCOUNT_V2_PLAN.md §9.12 "Identity", decision 27).
 *
 * 🔴 **One key per source, built once where the discount enters v2, compared only through
 * `sameOffer`.** v1 guessed instead, with `couponCode || discountCode || discountId || mid` at each
 * call site (19 chains in web's sheet); audit check T refuses a new chain anywhere in v2 but here.
 *
 * Keyed as the backend and the old FE key them (the evidence is in the plan):
 *
 * | source | key | why |
 * |---|---|---|
 * | a catalogue deal, a manual discount, a reward, an auto deal | `deal:<discountId>` | the backend's document `_id`: `mid` in the catalogue, `discountId` on the bill |
 * | a typed code, any provider; a catalogue deal in coupon mode | `code:<code>`, case ignored | a code is exercised and released by its code; web v1 and the old FE compare it case-insensitively |
 * | a partner's listed offer | `partner:<metaId>:<its id on the bill>` | the merchant's document id when the partner lists a merchant deal (`mid`, Exclusife), else the code the backend releases it by (`couponCode`, else `discountCode`). The edge writes it into the entry's `discountId` (web v1 and `buildEntry`: `mid || discountId`, after the source put the code in `discountId`), and the pricing engine keeps `discountId`, so the key survives a partner minting a new coupon code at apply (DotPe) and keeps two coupons of one deal apart |
 * | an item discount | `line:<lineId>:<discountId>` | one discount on one line (decision 30) |
 * | the points line | `points:<metaId>` | one per partner on a bill (built with the points line, R2.4d) |
| an entry known only by its name | `named:<discountCode>` | an entry with no id, no code and no partner, kept only so a cashier can see and take off what the cap counts (decision 102) |
 *
 * The code a row SHOWS is a separate value (a source row's `displayCode`, by each partner's `offerCodeField`: `discounts/sources/partner.ts`).
 * "Is this code already on the bill?" is a separate rule too (`billHasCode`): a code applied by
 * typing it and the same coupon listed by its partner are one coupon with two keys.
 */

/** A discount's identity. Built only by the functions below. */
export type OfferKey = string & { readonly __offerKey: true };

// The one place a string becomes an `OfferKey`; every builder below goes through it.
const asKey = (key: string): OfferKey => key as OfferKey;

const nonEmpty = (value: unknown): string | null =>
  value === undefined || value === null || String(value).trim() === "" ? null : String(value).trim();

export const dealKey = (discountId: string): OfferKey => asKey(`deal:${discountId}`);
export const codeKey = (code: string): OfferKey => asKey(`code:${code.trim().toLowerCase()}`);
export const partnerKey = (metaId: string, idOnTheBill: string): OfferKey => asKey(`partner:${metaId}:${idOnTheBill}`);
export const lineKey = (lineId: string, discountId: string): OfferKey => asKey(`line:${lineId}:${discountId}`);
export const pointsKey = (metaId: string): OfferKey => asKey(`points:${metaId}`);
/** An entry known only by its `discountCode` (decision 102); `entryKey`'s last resort. */
export const namedKey = (discountCode: string): OfferKey => asKey(`named:${discountCode.trim()}`);

/** The two keys name the same discount. A missing key names nothing, so it matches nothing. */
export const sameOffer = (a: OfferKey | null, b: OfferKey | null): boolean => a !== null && a === b;

/**
 * The code a partner's offer is released by, and so its identity: its `couponCode`, else its
 * `discountCode` (the backend's release key, `sale_customer_service.js` 775-848; the old FE's
 * release, `ItemEditController.js` 1654). Every listing partner sends one of the two (partners.md
 * §2: DotPe and Xeno and Blue Tokai a coupon code, Reelo, Fudr, EasyRewardz and Exclusife a
 * discount code).
 */
export const listedCode = (offer: DiscountOffer): string | null => nonEmpty(offer.couponCode) ?? nonEmpty(offer.discountCode);

/** A deal from the merchant's catalogue (keyed by `mid`); a coupon-mode deal is pressed as its code. */
export function catalogueOfferKey(offer: DiscountOffer): OfferKey | null {
  if (offer.mode === DISCOUNT_MODE.coupon) {
    const code = listedCode(offer);
    return code ? codeKey(code) : null;
  }
  const id = nonEmpty(offer.mid);
  return id ? dealKey(id) : null;
}

/** A code the cashier types (`/coupon/verify`), whoever issued it. */
export const typedCodeKey = (code: string): OfferKey | null => (nonEmpty(code) ? codeKey(code) : null);

/**
 * What a partner's listed offer is called on the bill: the merchant's document id when the partner
 * lists one of the merchant's own deals (Exclusife sends `mid`, `api_exclusife_service.js` 1072-1074),
 * else its listed code. The same value the edge writes into the entry's `discountId`.
 */
export const partnerOfferId = (offer: DiscountOffer): string | null => nonEmpty(offer.mid) ?? listedCode(offer);

/**
 * The id an offer is written onto the bill with (`discountId`): the catalogue's `mid` (the backend's
 * `_id`), else the `discountId` its source gave it (a partner offer's id on the bill, a code check's
 * discount). Web v1 and `buildEntry` both write `mid || discountId`.
 */
export const idOnTheBill = (offer: DiscountOffer): string | null => nonEmpty(offer.mid) ?? nonEmpty(offer.discountId);

/**
 * The coupon's own document id in the code check's answer (`/coupon/verify`): `couponId`, or the
 * document's `mid` / `_id` when the answer is the coupon record itself. Written as `couponId`
 * (decision 10), which the pricing engine keeps.
 */
export const couponIdOf = (answer: Record<string, unknown> | undefined): string | null =>
  nonEmpty(answer?.couponId) ?? nonEmpty(answer?.mid) ?? nonEmpty(answer?._id);

/** An offer a partner listed (`/sale/customer/offers`). */
export function partnerOfferKey(metaId: string | undefined, offer: DiscountOffer): OfferKey | null {
  const id = partnerOfferId(offer);
  return metaId && id ? partnerKey(metaId, id) : null;
}

/** Is this bill entry a partner's listed offer (`isExternal`, or `source: external`)? */
export function isPartnerEntry(entry: BillDiscount): boolean {
  const discount = entry as Record<string, unknown>;
  return discount.isExternal === true || discount.source === EXTERNAL_SOURCE;
}

/**
 * An entry already on the bill, read after any number of prices (the engine keeps `discountId`,
 * `couponCode`, `couponProvider`, `isExternal`, `source` and `mode`: bill-entry-readers.md §3).
 *
 * - a partner's listed offer (`isExternal`, or `source: external`): its id on the bill
 *   (`partnerOfferId`) rides in `discountId`, where the edge wrote it;
 * - any other entry with a `couponCode` came from a code;
 * - any other entry with a `discountId` is a deal of the merchant's, by its id;
 * - an entry with none of those but a `discountCode` is known by that name (decision 102).
 *
 * 🔴 **The last branch exists for the edited points line.** A cashier once edited a points line in
 * v1, which stripped its `discountId` and its `loyaltyPoints`, and left a manual discount the
 * merchant's cap still counts. With no key v2 could neither list it nor take it off, so the slot's
 * holder was invisible (decisions 96, 102). Now it is listed, with its Remove.
 *
 * One edge: two such entries of the same code share a key, so they list as one row (their amounts
 * summed) and Remove takes both off. Better one row that clears the slot than a second one nothing can name.
 */
export function entryKey(entry: BillDiscount): OfferKey | null {
  const discount = entry as Record<string, unknown>;
  const discountId = nonEmpty(discount.discountId);
  const provider = nonEmpty(discount.couponProvider);
  if (isPartnerEntry(entry)) {
    return provider && discountId ? partnerKey(provider, discountId) : null;
  }
  const code = nonEmpty(discount.couponCode);
  if (code) return codeKey(code);
  if (discountId) return dealKey(discountId);
  const name = nonEmpty(discount.discountCode);
  return name ? namedKey(name) : null;
}

/** The bill's entry with this key, if any. */
export const findEntry = (sale: Sale | undefined, key: OfferKey | null): BillDiscount | undefined =>
  (sale?.billDiscountList ?? []).find((entry: BillDiscount) => sameOffer(entryKey(entry), key));

/** Is this code on the bill already, on any entry, case ignored (web v1 `applyCoupon`; the old FE 1257)? */
export function billHasCode(sale: Sale | undefined, code: string): boolean {
  const wanted = nonEmpty(code)?.toLowerCase();
  if (!wanted) return false;
  return (sale?.billDiscountList ?? []).some(
    (entry: BillDiscount) => nonEmpty((entry as Record<string, unknown>).couponCode)?.toLowerCase() === wanted,
  );
}
